import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CalendarRange } from "lucide-react";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return s;
  }
};

export const AdminStatements = ({ client, api }) => {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get(`${api}/qv2/customers`);
        setCustomers(data);
      } catch {}
    })();
  }, [client, api]);

  const generate = async () => {
    if (!customerId || !month) return toast.error("Pick a customer and month");
    setBusy(true);
    try {
      const { data } = await client.get(`${api}/qv2/statement`, { params: { customer_id: customerId, month } });
      setData(data);
    } catch {
      toast.error("Could not generate statement");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="statements-panel">
      <div className="bg-steel border border-white/10 p-5 mb-6 flex flex-wrap items-end gap-4">
        <div>
          <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke block mb-2">Customer</label>
          <select data-testid="statement-customer-select" value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="bg-ink border border-white/20 font-mono text-sm text-white px-3 py-2.5 focus:outline-none min-w-[240px]">
            <option value="">Select…</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.company}</option>)}
          </select>
        </div>
        <div>
          <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke block mb-2">Month</label>
          <input data-testid="statement-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="bg-ink border border-white/20 font-mono text-sm text-white px-3 py-2.5 focus:outline-none [color-scheme:dark]" />
        </div>
        <button data-testid="statement-generate-button" onClick={generate} disabled={busy} className="btn-sweep btn-navy font-heading font-semibold uppercase tracking-wider text-sm px-6 py-2.5 flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : "Generate"}
        </button>
      </div>

      {!data ? (
        <div data-testid="statement-empty" className="bg-steel border border-white/10 p-14 text-center">
          <CalendarRange size={40} className="mx-auto text-smoke" strokeWidth={1.5} />
          <p className="mt-4 font-mono text-xs tracking-[0.3em] uppercase text-smoke">Pick a customer & month</p>
          <p className="mt-2 font-body text-sm text-smoke">You'll see confirmed orders, bills raised, and outstanding balance.</p>
        </div>
      ) : (
        <div data-testid="statement-result">
          <p className="font-heading font-semibold uppercase text-xl tracking-wide text-white mb-4">
            {data.customer.company} · {new Date(data.month + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-steel border border-white/10 p-5">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Orders (month)</p>
              <p data-testid="statement-order-value" className="font-heading font-semibold text-2xl text-white mt-1">{fmt(data.totals.order_value)}</p>
            </div>
            <div className="bg-steel border border-white/10 p-5">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Billed (month)</p>
              <p data-testid="statement-billed" className="font-heading font-semibold text-2xl text-white mt-1">{fmt(data.totals.billed)}</p>
            </div>
            <div className="bg-steel border border-white/10 p-5">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Received (month)</p>
              <p data-testid="statement-received" className="font-heading font-semibold text-2xl text-green-300 mt-1">{fmt(data.totals.received)}</p>
            </div>
            <div className="bg-steel border border-white/10 p-5">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Outstanding (all-time)</p>
              <p data-testid="statement-outstanding" className="font-heading font-semibold text-2xl text-red-300 mt-1">{fmt(data.totals.outstanding)}</p>
            </div>
          </div>

          <div className="border border-white/10 bg-steel overflow-x-auto mb-6">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke px-4 pt-4">Confirmed Orders ({data.orders.length})</p>
            <table data-testid="statement-orders-table" className="w-full min-w-[640px] mt-2">
              <thead>
                <tr className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke text-left">
                  <th className="py-2 px-4 font-medium">Quote #</th>
                  <th className="py-2 px-4 font-medium">Date</th>
                  <th className="py-2 px-4 font-medium">Weight</th>
                  <th className="py-2 px-4 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.orders.length === 0 ? (
                  <tr><td colSpan={4} className="py-4 px-4 font-body text-sm text-smoke">No confirmed orders this month.</td></tr>
                ) : data.orders.map((o) => (
                  <tr key={o.quote_no} className="border-t border-white/10">
                    <td className="py-3 px-4 font-mono text-xs text-[#7FB3E8]">{o.quote_no}</td>
                    <td className="py-3 px-4 font-mono text-xs text-smoke">{fmtDate(o.created_at)}</td>
                    <td className="py-3 px-4 font-mono text-xs text-smoke">{o.total_kg} kg</td>
                    <td className="py-3 px-4 font-mono text-xs text-white text-right">{fmt(o.grand_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border border-white/10 bg-steel overflow-x-auto mb-6">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke px-4 pt-4">Sale Bills ({data.bills.length})</p>
            <table data-testid="statement-bills-table" className="w-full min-w-[720px] mt-2">
              <thead>
                <tr className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke text-left">
                  <th className="py-2 px-4 font-medium">Bill #</th>
                  <th className="py-2 px-4 font-medium">Date</th>
                  <th className="py-2 px-4 font-medium text-right">Total</th>
                  <th className="py-2 px-4 font-medium text-right">Paid</th>
                  <th className="py-2 px-4 font-medium text-right">Due</th>
                </tr>
              </thead>
              <tbody>
                {data.bills.length === 0 ? (
                  <tr><td colSpan={5} className="py-4 px-4 font-body text-sm text-smoke">No bills raised this month.</td></tr>
                ) : data.bills.map((b) => (
                  <tr key={b.id} className="border-t border-white/10">
                    <td className="py-3 px-4 font-mono text-xs text-[#7FB3E8]">{b.bill_no}</td>
                    <td className="py-3 px-4 font-mono text-xs text-smoke">{b.bill_date}</td>
                    <td className="py-3 px-4 font-mono text-xs text-white text-right">{fmt(b.total)}</td>
                    <td className="py-3 px-4 font-mono text-xs text-green-300 text-right">{fmt(b.paid)}</td>
                    <td className="py-3 px-4 font-mono text-xs text-red-300 text-right">{fmt(b.due)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.payments.length > 0 && (
            <div className="border border-white/10 bg-steel overflow-x-auto">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke px-4 pt-4">Payments Received ({data.payments.length})</p>
              <table data-testid="statement-payments-table" className="w-full min-w-[640px] mt-2">
                <thead>
                  <tr className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke text-left">
                    <th className="py-2 px-4 font-medium">Date</th>
                    <th className="py-2 px-4 font-medium">Bill #</th>
                    <th className="py-2 px-4 font-medium">Method</th>
                    <th className="py-2 px-4 font-medium text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.payments.map((p) => (
                    <tr key={p.id} className="border-t border-white/10">
                      <td className="py-3 px-4 font-mono text-xs text-smoke">{p.date}</td>
                      <td className="py-3 px-4 font-mono text-xs text-[#7FB3E8]">{p.bill_no}</td>
                      <td className="py-3 px-4 font-mono text-xs text-smoke">{p.method}{p.reference ? ` · ${p.reference}` : ""}</td>
                      <td className="py-3 px-4 font-mono text-xs text-green-300 text-right">{fmt(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
